import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { isEmpty } from 'lodash'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import Input from 'src/components/Elements/Inputs/Input'
import Button from 'src/components/Elements/Button'
import { useForm, Controller } from 'react-hook-form'
import { Link } from 'react-router-dom'
import Cookies from 'js-cookie'
import classNames from 'classnames'
import useLogin from 'src/queries/auth/useLogin'
import Loader from 'src/components/Elements/Loader'
import { getRedirectUrl } from 'src/utils/helpers/RedirectHelpers'
import generalStyles from 'src/assets/styles/generalStyles.module.scss'
import preloginStyles from './prelogin.module.scss'
import fontStyles from 'src/assets/styles/fontStyles.module.scss'

const SignInForm = () => {
  const { mutateAsync, isLoading } = useLogin()
  const [data, setData] = useState(null)
  const [loginError, setLoginError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const redirectUrl = useMemo(() => getRedirectUrl(), [])

  const {
    // reset, for clearing cookie?
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch
  } = useForm({
    defaultValues: {
      email: Cookies.get('email') || '',
      password: ''
    }
  })

  // checks if loading keeps happening after half a minute which means there's a network error
  useEffect(() => {
    let isLoadingTimer

    // eslint-disable-next-line prefer-const
    isLoadingTimer = setTimeout(() => {
      if (isLoading) {
        setLoginError('Network Error')
      }
    }, 3000)

    return () => {
      clearTimeout(isLoadingTimer)
    }
  }, [isLoading])

  const login = async (data) => {
    const response = await mutateAsync({
      email: data.email,
      password: data.password,
      redirectUrl: redirectUrl
    })
    if (response.status < 200 || response.status > 300) {
      const errorMessage =
        typeof response.data !== 'string'
          ? Object.values(response.data)[0].join(', ')
          : response.data
      if (!isEmpty(errorMessage)) {
        setLoginError(errorMessage === 'account_disabled'
          ? `This account is no longer active. Contact <a href='/auth/contact'>support</a> to reactivate your account.`
          : 'Invalid login credentials'
        )
      }
    }
  }

  const handleKeyDown = useCallback(
    (e) => {
      if (e.code === 'Enter') {
        login(data)
      }
    },
    [data, login]
  )

  const IconComponent = showPassword ? FaEye : FaEyeSlash

  return (
    <div className={preloginStyles.formContainer} data-testid="preloginForm">
      <div>
        <Controller
          name="email"
          control={control}
          rules={{
            required: {
              value: true
            }
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              id="email"
              label={{ label: 'Email', placeholder: 'Email' }}
              name="email"
              type="email"
              icon={{ name: 'Email', size: 26 }}
              isLogin={true}
              onBlur={onBlur}
              value={value}
              onChange={(text) => {
                onChange(text)
                setData({ ...watch() })
              }}
            />
          )}
        />
        <div className={preloginStyles.inputGap} />
        <Controller
          name="password"
          control={control}
          rules={{
            required: {
              value: true
            }
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <div className={classNames(generalStyles.flexRow)}>
              <Input
                id="password"
                name="password"
                label={{ label: 'Password', placeholder: 'Password' }}
                type={showPassword ? 'text' : 'password'}
                icon={{ name: 'Password', size: 27 }}
                onBlur={onBlur}
                value={value}
                fullWidth
                onChange={(text) => {
                  onChange(text)
                  setData({ ...watch() })
                }}
                onKeyDown={handleKeyDown}
                error={
                  JSON.stringify(errors) !== '{}' || loginError
                    ? loginError || 'Invalid login credentials'
                    : null
                }
              />
              <IconComponent
                data-testid='passwordVisibilityToggle'
                style={{ marginTop: 15 }}
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
          )}
        />
        <div className={generalStyles.mt15}>
          <Link className={fontStyles.pxsmallboldlsssin01lightbasegraphik12pt} to="/auth/forgot-password" data-testid="forgotPasswordLink">
            Forgot Password?
          </Link>
        </div>
      </div>
      <div className={preloginStyles.btnContainer}>
        {isLoading && loginError !== 'Network Error' ? (
          <Loader />
        ) : (
          <Button
            color={isValid ? 'lightBase' : 'lightBaseOutline'}
            size="large"
            onClick={handleSubmit(login)}
            label="Sign In"
            disabled={!isValid}
            id="submitLogin"
            style={loginError ? { marginTop: 0 } : { marginTop: 30 }}
          />
        )}
      </div>
      <div className={preloginStyles.actionTextRow}>
        <span
          className={classNames(
            fontStyles.pxsmallboldlsssin08blackgraphik12pt,
            preloginStyles.notMember
          )}
        >
          Not a Member? <wbr />
          <a
            href="https://www.evanta.com/apply#apply"
            target="_blank"
            rel="noreferrer"
            className={fontStyles.pxsmallboldlsssin01lightbasegraphik12pt}
          >
            Apply to Join
          </a>
        </span>
        <Link to="/auth/help" className={fontStyles.pxsmallboldlsssin01lightbasegraphik12pt} >
          Help & Support
        </Link>
      </div>
    </div>
  )
}

export default SignInForm
